import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateSubscription, useUpdateSubscription } from '@/hooks/finance/useRecurringSubscriptions';
import { useExpenseCategories } from '@/hooks/finance/useExpenseCategories';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { useAllProfiles } from '@/hooks/use-profile';
import { RecurringSubscription, SubscriptionFrequency, VatMode } from '@/types/database/recurring-subscriptions';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription?: RecurringSubscription | null;
}

export default function NewSubscriptionDialog({ open, onOpenChange, subscription }: Props) {
  const isEdit = !!subscription;
  const createMutation = useCreateSubscription();
  const updateMutation = useUpdateSubscription();
  const { data: categories } = useExpenseCategories();
  const { data: bankAccounts } = useBankAccounts(true);
  const { data: profiles } = useAllProfiles();

  const [form, setForm] = useState({
    name: '',
    description: '',
    vendor_name: '',
    amount: '',
    currency: 'ETB',
    category_id: '',
    department_id: '',
    bank_account_id: '',
    frequency: 'monthly' as SubscriptionFrequency,
    next_due_date: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    vat_mode: 'none' as VatMode,
    payment_method: '',
    assigned_to: '',
    assigned_name: '',
  });

  const [assignMode, setAssignMode] = useState<'user' | 'name'>('user');
  useEffect(() => {
    if (subscription) {
      const hasAssignedTo = !!subscription.assigned_to;
      const hasAssignedName = !!subscription.assigned_name;
      setAssignMode(hasAssignedName && !hasAssignedTo ? 'name' : 'user');
      setForm({
        name: subscription.name,
        description: subscription.description || '',
        vendor_name: subscription.vendor_name || '',
        amount: String(subscription.amount),
        currency: subscription.currency || 'ETB',
        category_id: subscription.category_id || '',
        department_id: subscription.department_id || '',
        bank_account_id: subscription.bank_account_id || '',
        frequency: subscription.frequency,
        next_due_date: subscription.next_due_date,
        start_date: subscription.start_date,
        end_date: subscription.end_date || '',
        vat_mode: subscription.vat_mode || 'none',
        payment_method: subscription.payment_method || '',
        assigned_to: subscription.assigned_to || '',
        assigned_name: subscription.assigned_name || '',
      });
    } else {
      setAssignMode('user');
      setForm({
        name: '', description: '', vendor_name: '', amount: '', currency: 'ETB',
        category_id: '', department_id: '', bank_account_id: '',
        frequency: 'monthly', next_due_date: '', start_date: new Date().toISOString().split('T')[0],
        end_date: '', vat_mode: 'none', payment_method: '', assigned_to: '', assigned_name: '',
      });
    }
  }, [subscription, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const input = {
      name: form.name,
      description: form.description || undefined,
      vendor_name: form.vendor_name || undefined,
      amount: parseFloat(form.amount),
      currency: form.currency,
      category_id: form.category_id || undefined,
      department_id: form.department_id || undefined,
      bank_account_id: form.bank_account_id || undefined,
      frequency: form.frequency,
      next_due_date: form.next_due_date,
      start_date: form.start_date,
      end_date: form.end_date || undefined,
      vat_mode: form.vat_mode,
      payment_method: form.payment_method || undefined,
      assigned_to: assignMode === 'user' ? (form.assigned_to || undefined) : undefined,
      assigned_name: assignMode === 'name' ? (form.assigned_name || undefined) : undefined,
    };

    if (isEdit) {
      await updateMutation.mutateAsync({ id: subscription!.id, ...input });
    } else {
      await createMutation.mutateAsync(input);
    }
    onOpenChange(false);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Subscription' : 'New Recurring Subscription'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="name">Name *</Label>
              <Input id="name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="col-span-2">
              <Label htmlFor="vendor">Vendor</Label>
              <Input id="vendor" value={form.vendor_name} onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="amount">Amount *</Label>
              <Input id="amount" type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required />
            </div>
            <div>
              <Label htmlFor="currency">Currency</Label>
              <Select value={form.currency} onValueChange={v => setForm(f => ({ ...f, currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ETB">ETB</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="frequency">Frequency *</Label>
              <Select value={form.frequency} onValueChange={v => setForm(f => ({ ...f, frequency: v as SubscriptionFrequency }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="vat_mode">VAT Mode</Label>
              <Select value={form.vat_mode} onValueChange={v => setForm(f => ({ ...f, vat_mode: v as VatMode }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="add">Add VAT</SelectItem>
                  <SelectItem value="deduct">Deduct VAT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="next_due_date">Next Due Date *</Label>
              <Input id="next_due_date" type="date" value={form.next_due_date} onChange={e => setForm(f => ({ ...f, next_due_date: e.target.value }))} required />
            </div>
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input id="start_date" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input id="end_date" type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={form.category_id} onValueChange={v => setForm(f => ({ ...f, category_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories?.filter(c => c.is_recordable !== false).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bank_account">Bank Account</Label>
              <Select value={form.bank_account_id} onValueChange={v => setForm(f => ({ ...f, bank_account_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {bankAccounts?.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="payment_method">Payment Method</Label>
              <Select value={form.payment_method} onValueChange={v => setForm(f => ({ ...f, payment_method: v }))}>
                <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="assigned_to">Assigned To</Label>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                  onClick={() => {
                    setAssignMode(m => m === 'user' ? 'name' : 'user');
                    setForm(f => ({ ...f, assigned_to: '', assigned_name: '' }));
                  }}
                >
                  {assignMode === 'user' ? 'Not in system? Type a name' : 'Select from users'}
                </button>
              </div>
              {assignMode === 'user' ? (
                <Select value={form.assigned_to} onValueChange={v => setForm(f => ({ ...f, assigned_to: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select person" /></SelectTrigger>
                  <SelectContent>
                    {profiles?.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.first_name} {p.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="assigned_name"
                  placeholder="Enter person's name"
                  value={form.assigned_name}
                  onChange={e => setForm(f => ({ ...f, assigned_name: e.target.value }))}
                />
              )}
            </div>
            <div className="col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isPending}>{isPending ? 'Saving...' : isEdit ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
